import { AI_FIELDS } from "./aiReview";

const nm = (v) => String(v ?? "").replace(/[\s\-_.:/]/g, "").toLowerCase();
const empty = (f, key) => !f || f.value === "" || f.value == null || f.fb === "removed" || (key === "qty" && !f.box);
const natural = (a, b) => String(a).localeCompare(String(b), "lt", { numeric: true });
export const groupKey = (it) => it.group || `p${it.page}`;

// Groups for the "joined positions from several pages" panel.
export function deriveGroups(items) {
    const map = new Map();
    items.forEach((it) => {
        const k = groupKey(it);
        const g = map.get(k) || { key: k, label: "", pages: new Set(), names: [], ids: [] };
        g.label = g.label || it.block || "";
        (it.pages?.length ? it.pages : [it.page]).forEach((p) => g.pages.add(p));
        Object.values(it.f).forEach((f) => { if (f?.page) g.pages.add(f.page); });
        const n = it.f.name?.value;
        if (n !== "" && n != null) g.names.push(String(n));
        g.ids.push(it._id);
        map.set(k, g);
    });
    return [...map.values()].map((g) => {
        const names = [...g.names].sort(natural);
        return { ...g, pages: [...g.pages].sort((a, b) => a - b), first: names[0] || "", last: names[names.length - 1] || "" };
    }).sort((a, b) => (b.pages.length > 1) - (a.pages.length > 1) || a.pages[0] - b.pages[0]);
}

export const rangeOf = (g) => (g.first && g.last && g.first !== g.last ? `${g.first} – ${g.last}` : g.first || "—");

function combine(list) {
    const sorted = [...list].sort((a, b) => a.page - b.page);
    const base = { ...sorted[0], f: { ...sorted[0].f } };
    AI_FIELDS.forEach(({ key }) => {
        if (!empty(base.f[key], key)) return;
        const o = sorted.find((x) => x !== sorted[0] && !empty(x.f[key], key));
        if (o) base.f[key] = { ...o.f[key], page: o.f[key].page || o.page };
    });
    base.errors = [...new Set(sorted.flatMap((x) => x.errors || []))];
    base.pages = [...new Set(sorted.flatMap((x) => x.pages?.length ? x.pages : [x.page]))].sort((a, b) => a - b);
    base.src = sorted.flatMap((x) => (x.src?.length ? x.src : [{ ...x, src: undefined, pages: undefined }]));
    return base;
}

// Join several groups into one; same-named positions on different pages become one position.
export function mergeGroups(items, keys) {
    const target = keys[0].replace(/~p\d+$/, "");
    const label = items.find((it) => keys.includes(groupKey(it)) && it.block)?.block || "";
    const inG = (it) => keys.includes(groupKey(it));
    const buckets = new Map();
    const out = [];
    items.forEach((it) => {
        if (!inG(it)) { out.push(it); return; }
        const n = nm(it.f.name?.value);
        const moved = { ...it, group: target, block: label };
        if (!n) { out.push(moved); return; }
        const b = buckets.get(n);
        const pagesOf = (x) => (x.pages?.length ? x.pages : [x.page]);
        const clash = b && b.some((x) => pagesOf(x).some((p) => pagesOf(moved).includes(p)));
        if (!b || clash) { buckets.set(clash ? `${n}#${it._id}` : n, [moved]); out.push(moved); return; }
        b.push(moved);
    });
    const replace = new Map();
    buckets.forEach((list) => { if (list.length > 1) { const c = combine(list); list.forEach((x) => replace.set(x._id, x === list[0] ? c : null)); } });
    return out.map((it) => (replace.has(it._id) ? replace.get(it._id) : it)).filter(Boolean);
}

// Undo a cross-page join: every page becomes its own group, joined positions are split back per page.
export function splitGroup(items, key) {
    let next = Math.max(-1, ...items.map((x) => x._id)) + 1;
    return items.flatMap((it) => {
        if (groupKey(it) !== key) return [it];
        const parts = it.src?.length > 1 ? it.src.map((s, i) => ({ ...s, _id: i === 0 ? it._id : next++, include: it.include })) : [it];
        return parts.map((p) => ({ ...p, src: undefined, pages: [p.page], group: `${key.replace(/~p\d+$/, "")}~p${p.page}`, block: it.block || "" }));
    });
}
